import React, { Component } from 'react';
import { withAsyncHandlers, asyncConnect } from 'react-async-client';
import { Modal, Row, Col, Button, Spin } from 'antd';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import Translate from 'react-translate-component';
import { head, isEmpty, path, toLower } from 'ramda';
import { withState } from 'recompose';

import { metroUsers } from 'constants/dictionaries'
import withFormWrapper from 'components/hocs/withFormWrapper';
import { required } from 'utils/validators';
import { getUser, getMetroRequest, postMetroRequestRoute } from 'actions/asyncActions';
import { fullAutoOptions } from 'constants/asyncClient';
import ErrorHTML from 'components/common/form/ErrorHTML';
import Switch from 'components/common/form/Switch';
import { SearchInputLoaded } from 'components/common/form/SearchInput';

const stateToProps = state => ({
    user: getUser.selectData(state)
});

@withState('approvalRoute', 'setApprovalRoute', [])
@asyncConnect({
    getMetroRequest: getMetroRequest
        .withPayload(({ params: { id } }) => ({
            id,
        }))
        .withOptions(fullAutoOptions),
    postMetroRequestRoute: postMetroRequestRoute
        .withParams(() => ({ createRoute: 1 }))
        .withSuccessHandler(({ getMetroRequest }) => {
            getMetroRequest.refresh();
        })
        .withOptions({ resetOnUnmount: true }),
    formAction: postMetroRequestRoute
        .withOptions({ resetOnUnmount: true }),
}, stateToProps)
@withFormWrapper({
    mapPropsToValues: ({ params }) => ({
        objkey: params.id,
        createRoute: 0,
    }),
})
@withAsyncHandlers({
    getMetroRequest: {
        successHandler: ({ postMetroRequestRoute, form, params, setApprovalRoute, approvalRoute }, { payload }) => {
            if (params.draft && isEmpty(approvalRoute) && !postMetroRequestRoute.meta.success) {
                if (postMetroRequestRoute.meta.error || postMetroRequestRoute.meta.lastSucceedAt) {
                    return;
                }

                postMetroRequestRoute.dispatch({
                    objkey: params.id,
                    createRoute: 1,
                });
            } else {
                setApprovalRoute(payload.approvalRoute);

                form.batch(() => {
                    const values = (payload.approvalRoute || []).map(item => ({
                        id: item.id,
                        required: item.required,
                        approver: item.approver,
                        peopleId: item.approver.id,
                        approverName: head((item.approver.name || '').split(' ')),
                        notExcluded: !item.excluded,
                    }));

                    form.change('routes', values);
                });
            }
        },
    },
    formAction: {
        successHandler: ({ params, close }, { payload }) => {
            if (!payload.needContacts) {
                params.callback && params.callback();
                close();
            }
        },
    },
})
export default class MetroRequestRouteModal extends Component {
    renderFooter(approvalRoute) {
        const { formAction: { meta }, postMetroRequestRoute: { meta : initialMeta }, handleSubmit, close, params } = this.props;

        return (
            <Row>
                <Col xs={{ span: 14 }} className="alert-inline">
                    <ErrorHTML meta={meta} />
                </Col>
                <Col xs={{ span: 10 }}>
                    <Button onClick={close}>
                        <Translate content='events.cancel' />
                    </Button>
                    <Button type='primary' onClick={handleSubmit} disabled={meta.pending || initialMeta.pending || !approvalRoute}>
                        { params.edit ? (
                            <Translate content='modal_settings.save' />
                        ) : (
                            <Translate content='metro.send' />
                        )}
                    </Button>
                </Col>
            </Row>
        );
    }

    render() {
        const { params, modal, postMetroRequestRoute, approvalRoute } = this.props;
        const { getMetroRequest: { meta }, postMetroRequestRoute: { meta : initialMeta } } = this.props;

        return (
            <Modal
                {...modal}
                title={<Translate content='metro.requestRoute.title' />}
                footer={this.renderFooter(approvalRoute)}
            >
                <ErrorHTML meta={initialMeta} />
                <ErrorHTML meta={meta} />
                { meta.pending ? <Spin /> : approvalRoute ? (
                    <FieldArray name='routes'>
                        {({ fields }) => fields ? fields.map((name, index) => {
                            let item = path(['value', index], fields);
                            let routeId = path(['id'], item);
                            let requiredField = !!path(['required'], item);
                            let approverName = path(['approverName'], item);
                            let selectedApproverName = path(['approver', 'name'], item);
                            let value = path(['approver', 'id'], item);

                            return (
                                <Row key={name} className='m-b-20'>
                                    <Col span={3} className='text-center p-t-10'>
                                        <Field
                                            name={`${name}.notExcluded`}
                                            component={Switch}
                                            disabled={requiredField}
                                            notWrap
                                        />
                                    </Col>
                                    <Col span={21}>
                                        { value && !requiredField ? (
                                            <Field
                                                key={approverName}
                                                name={`${name}.peopleId`}
                                                component={SearchInputLoaded}
                                                loadName={metroUsers}
                                                searchLoadParams={search => ({
                                                    URLEncESSFilter: toLower(search)
                                                })}
                                                defaultSearch={approverName}
                                                disabled={requiredField}
                                                placeholderDefault
                                                placeholder={selectedApproverName || <Translate content='placeholders.chooseEmployee' />}
                                                inputTextPlaceholder='placeholders.inputNameText'
                                                notWrap
                                                validate={required}
                                                onChange={(value, item) => {
                                                    item && fields.update(index, {
                                                        id: routeId,
                                                        peopleId: value,
                                                        approver: {
                                                            id: value,
                                                            name: item.label,
                                                        },
                                                        approverName: item.label,
                                                        notExcluded: true,
                                                        required: 0,
                                                    });
                                                }}
                                                allowClear={false}
                                            />
                                        ) : (
                                            <div className='p-t-10'>
                                                {selectedApproverName}
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            );
                        }) : null}
                    </FieldArray>
                ) : (meta.success && !initialMeta.pending) && (
                    <Button onClick={() => postMetroRequestRoute.dispatch({ objkey: params.id, createRoute: 1 })}>
                        <Translate content='metro.request.createRoute' />
                    </Button>
                )}
            </Modal>
        );
    }
}
